import React from 'react';
import { OperatorSheet } from './OperatorSheet';
import { Button } from '../../../components/ui/Button';

/**
 * Asks before something that cannot be taken back, listing what it affects.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {string} props.title
 * @param {string} [props.message]
 * @param {Array<{key: string|number, title: string, subtitle?: string}>} [props.items]
 * @param {string} props.confirmLabel
 * @param {'primary'|'danger'} [props.confirmVariant='primary'] - Red only to delete.
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onClose - Also what the overlay and Escape do.
 * @param {string} [props.cancelLabel='Cancelar']
 * @param {boolean} [props.busy]
 * @param {string} [props.busyLabel]
 * @param {string} [props.testId='confirm-sheet']
 * @param {string} [props.confirmTestId] - The confirm button's; `${testId}-confirm` by default.
 * @param {React.ReactNode} [props.children] - What else to know before confirming, under the list.
 */
export const ConfirmSheet = ({
  isOpen,
  title,
  message,
  items = [],
  confirmLabel,
  confirmVariant = 'primary',
  onConfirm,
  onClose,
  cancelLabel = 'Cancelar',
  busy = false,
  busyLabel,
  testId = 'confirm-sheet',
  confirmTestId,
  children,
}) => (
  <OperatorSheet isOpen={isOpen} onClose={onClose} title={title} testId={testId} busy={busy}>
    <div className="space-y-4">
      {message && <p className="text-center text-ninja-dark dark:text-white">{message}</p>}
      {items.length > 0 && (
        <ul className="divide-y divide-border rounded-2xl border border-border">
          {items.map((item) => (
            <li key={item.key} data-testid={`${testId}-item`} className="px-4 py-3">
              <p className="font-semibold text-ninja-dark dark:text-white">{item.title}</p>
              {item.subtitle && <p className="text-sm text-text-secondary">{item.subtitle}</p>}
            </li>
          ))}
        </ul>
      )}
      {children}
      <div className="flex gap-3">
        <Button variant="ghost" size="lg" onClick={onClose} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          variant={confirmVariant}
          size="lg"
          fullWidth
          loading={busy}
          loadingLabel={busyLabel}
          onClick={onConfirm}
          data-testid={confirmTestId ?? `${testId}-confirm`}
        >
          {confirmLabel}
        </Button>
      </div>
    </div>
  </OperatorSheet>
);
